import capsule from "@/constants/capsule";
import {
  toLogsSmartAccount,
  ToLogsSmartAccountReturnType,
} from "@/pages/org/toLogsSmartAccount";
import {
  createCapsuleAccount,
  createCapsuleViemClient,
} from "@usecapsule/viem-v2-integration";
import { createContext, useContext, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { http, erc20Abi, toBytes, keccak256 } from "viem";
import { DEFAULT_CHAIN_ID } from "@/constants";
import usePublicClient, { chainConfig } from "@/hooks/usePublicClient";
import LoadingPage from "@/components/LoadingPage";
import { usdcInfo } from "@/constants/tokens";
import { getUserInfo, login, updateAvatar } from "@/api/login";
import { customSignMessage } from "@/constants/bundler";
import axios from "axios";
import { Country } from "country-state-city";
import CURRENCY_MAP from "@/constants/currencys";
import { getCustomer } from "@/api/bridge";
import { getDidName } from "@/utils/didName";

interface Capabilities {
  payin_crypto: "active" | string;
  payout_crypto: "active" | string;
  payin_fiat: "active" | string;
  payout_fiat: "active" | string;
}

interface BeneficialOwner {
  email: string;
  id: string;
  // 因为示例中beneficial_owners是一个数组但内容被省略了
  // 这里预留一个空接口，您可以根据实际数据补充属性
}
interface BusinessProfile {
  beneficial_owners: BeneficialOwner[];
  capabilities: Capabilities;
  created_at: string;
  email: string;
  endorsements: string[];
  first_name: string;
  future_requirements_due: string[];
  has_accepted_terms_of_service: boolean;
  id: string;
  last_name: string | null;
  persona_inquiry_type: "business" | string;
  rejection_reasons: { reason: string }[];
  type: string;
  status: string;
  tos_link: string;
  kyc_link: string;
  kyc_status: string;
  full_name: string;
}

const UserInfoContext = createContext<{
  showUsd: boolean;
  currencyInfo: {
    localCurrency: string;
    exchangeRate: number;
  };
  countryInfo: {
    countryIsoCode: string;
    countryName: string;
  };
  address: string;
  senderAddress: string;
  orgName: string;
  smartAccount: ToLogsSmartAccountReturnType | null;
  usdcBalance: string;
  taxRate: string;
  showBalance: boolean;
  avatarIndex: number;
  bridgeCustomer: BusinessProfile;
  setAddress: (address: string) => void;
  handleCheckUserStatus: () => void;
  handleSetOrgName: (name: string) => void;
  handleSwitchCurrency: () => void;
  handleSetCountryInfo: (countryIsoCode: string, setToLocal?: boolean) => void;
  handleSetCurrency: (currency: string, setToLocal?: boolean) => void;
  handleSetTaxRate: (rate: string) => void;
  handleShowBalance: (show: boolean) => void;
  handleSetAvatarIndex: (index: number) => void;
  handleGetCustomer: () => Promise<BusinessProfile>;
}>({
  showUsd: false,
  currencyInfo: {
    localCurrency: "",
    exchangeRate: 0,
  },
  countryInfo: {
    countryIsoCode: "",
    countryName: "",
  },
  address: "",
  senderAddress: "",
  orgName: "",
  smartAccount: null,
  usdcBalance: "0",
  taxRate: "3",
  showBalance: false,
  avatarIndex: 0,
  bridgeCustomer: {
    beneficial_owners: [],
    capabilities: {
      payin_crypto: "active",
      payout_crypto: "active",
      payin_fiat: "active",
      payout_fiat: "active",
    },
    created_at: "",
    email: "",
    endorsements: [],
    first_name: "",
    future_requirements_due: [],
    has_accepted_terms_of_service: false,
    id: "",
    last_name: "",
    persona_inquiry_type: "business",
    rejection_reasons: [],
    type: "",
    status: "",
    tos_link: "",
    kyc_link: "",
    kyc_status: "",
    full_name: "",
  },
  setAddress: () => {},
  handleCheckUserStatus: () => {},
  handleSetOrgName: () => {},
  handleSwitchCurrency: () => {},
  handleSetCountryInfo: () => {},
  handleSetCurrency: () => {},
  handleSetTaxRate: () => {},
  handleShowBalance: (show: boolean) => {},
  handleSetAvatarIndex: () => {},
  handleGetCustomer: () => new Promise<BusinessProfile>((resolve) => {}),
});

export const UserInfoProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const navigate = useNavigate();
  const [address, setAddress] = useState("");
  const [senderAddress, setSenderAddress] = useState("");
  const [init, setInit] = useState(false);
  const [taxRate, setTaxRate] = useState("3");
  const [smartAccount, setSmartAccount] =
    useState<ToLogsSmartAccountReturnType | null>(null);
  const [orgName, setOrgName] = useState("");
  const [avatarIndex, setAvatarIndex] = useState(0);
  const publicClient = usePublicClient();
  const [loading, setLoading] = useState(false);
  const { pathname } = useLocation();
  const [usdcBalance, setUsdcBalance] = useState("0");
  const [bridgeCustomer, setBridgeCustomer] = useState<BusinessProfile>({
    beneficial_owners: [],
    capabilities: {
      payin_crypto: "active",
      payout_crypto: "active",
      payin_fiat: "active",
      payout_fiat: "active",
    },
    created_at: "",
    email: "",
    endorsements: [],
    first_name: "",
    future_requirements_due: [],
    has_accepted_terms_of_service: false,
    id: "",
    last_name: "",
    persona_inquiry_type: "business",
    rejection_reasons: [],
    type: "",
    status: "",
    tos_link: "",
    kyc_link: "",
    kyc_status: "",
    full_name: "",
  });
  const [showBalance, setShowBalance] = useState(
    localStorage.getItem("showBalance") === "true"
  );
  const [currencyInfo, setCurrencyInfo] = useState({
    localCurrency: "",
    exchangeRate: 0,
  });
  const [countryInfo, setCountryInfo] = useState({
    countryIsoCode: "",
    countryName: "",
  });
  const [showUsd, setShowUsd] = useState(
    localStorage.getItem("showUsd") === "true"
  );

  const handleSetAvatarIndex = (index: number) => {
    setAvatarIndex(index);
  };

  const handleShowBalance = (show: boolean) => {
    setShowBalance(show);
    localStorage.setItem("showBalance", show.toString());
  };

  const handleSetTaxRate = (rate: string) => {
    setTaxRate(rate);
    localStorage.setItem("taxRate", rate);
  };

  // 获取IP对应的货币代码 获取汇率
  const fetchCurrencyInfo = async () => {
    try {
      const storageCurrency = localStorage.getItem("localCurrency");
      const storageCountry = localStorage.getItem("localCountry");
      let localCurrency = "";
      let localCountry = "";

      const { data: ipData } = await axios.get("https://ipapi.co/json/");
      localCountry = ipData.country_code;

      if (storageCountry) {
        localCountry = storageCountry;
      } else {
        localCountry = ipData.country_code;
      }

      const countryData = Country.getCountryByCode(localCountry);
      setCountryInfo({
        countryIsoCode: countryData?.isoCode || "",
        countryName: countryData?.name || "",
      });

      if (storageCurrency) {
        localCurrency = storageCurrency;
      } else {
        localCurrency = ipData.currency;
      }

      handleSetCurrency(localCurrency, false);
    } catch (error) {
      handleSetCurrency("USD", false);
    }
  };

  const handleSetCurrency = async (currency: string, setToLocal?: boolean) => {
    const { data: rateData } = await axios.get(
      `https://open.er-api.com/v6/latest/USD`
    );
    let _currency = currency;
    if (!rateData.rates[currency] || !CURRENCY_MAP[currency]) {
      _currency = "USD";
    }
    const exchangeRate = rateData.rates[_currency];
    if (setToLocal) {
      localStorage.setItem("localCurrency", _currency);
      setShowUsd(false);
    }
    setCurrencyInfo({ localCurrency: _currency, exchangeRate });
  };

  const handleSetCountryInfo = (
    countryIsoCode: string,
    setToLocal?: boolean
  ) => {
    const countryData = Country.getCountryByCode(countryIsoCode);
    setCountryInfo({
      countryIsoCode,
      countryName: countryData?.name || "",
    });
    if (setToLocal) {
      localStorage.setItem("localCountry", countryIsoCode);
    }
  };

  const handleInit = async () => {
    await handleCheckUserStatus();
    setInit(true);
  };

  const handleCheckUserStatus = async () => {
    setLoading(true);

    const isActive = await capsule.isSessionActive();
    if (isActive) {
      localStorage.setItem("isLogin", "true");
    }

    if (!isActive) {
      const isLoginBefore = localStorage.getItem("isLogin");
      if (isLoginBefore !== "true") {
        navigate("/login");
        setLoading(false);
        return;
      }

      try {
        await capsule.setWallets({});
        const authUrl = await capsule.refreshSession(false);
        const popup = window.open(authUrl, "Auth");

        // Wait for authentication to complete
        const { isComplete, isError } = await capsule.waitForLoginAndSetup(
          popup as Window | undefined
        );
        console.log(isComplete, isError, "isComplete");
        if (!isComplete || isError) {
          throw new Error("Session refresh failed");
        }
      } catch (error) {
        console.error("Session refresh failed:", error);
        localStorage.removeItem("jwtToken");
        // capsule.logout();
        setAddress("");
        setLoading(false);
        navigate("/login");
        localStorage.setItem("isLogin", "false");
        return;
      }
    }

    try {
      const wallets = capsule.getWallets();
      const walletKeys = Object.keys(wallets);
      const wallet = wallets[walletKeys[0]];

      await new Promise((resolve) => setTimeout(resolve, 1000));

      const messageOrg = "hello world";
      const message = keccak256(toBytes(messageOrg));

      const signature = await customSignMessage(capsule, message);

      // const recoveredAddressEthers = ethers.verifyMessage(
      //   ethers.getBytes(message), // 将十六进制消息转换为字节数组
      //   signature
      // );

      // const recoveredAddress = await recoverMessageAddress({
      //   message: {
      //     raw: message,
      //   }, // 直接传入原始消息
      //   signature: signature,
      // });

      console.log("start login");
      const { token } = await login({
        message: messageOrg,
        signature: signature,
        address: wallet.address as string,
      });

      localStorage.setItem("jwtToken", token);

      const userInfo = await getUserInfo();

      const capsuleAccount = createCapsuleAccount(
        capsule,
        wallet.address as `0x${string}`
      );

      const smartAccountOwner = createCapsuleViemClient(capsule, {
        chain: chainConfig[DEFAULT_CHAIN_ID],
        transport: http(),
        account: capsuleAccount,
      });

      const account: ToLogsSmartAccountReturnType = await toLogsSmartAccount({
        client: publicClient,
        owner: smartAccountOwner,
        homeChain: BigInt(DEFAULT_CHAIN_ID),
        homeCoin: usdcInfo.address as `0x${string}`,
        swapper: usdcInfo.address as `0x${string}`,
        bridger: usdcInfo.address as `0x${string}`,
        salt: BigInt(0),
      });

      const senderAddress = await account.getAddress();

      const customerData: any = await getCustomer();
      setBridgeCustomer(customerData);

      let orgName = getDidName(userInfo.didName);
      setOrgName(orgName);
      setAddress(wallet.address as string);
      setSenderAddress(senderAddress);
      setSmartAccount(account);
      setAvatarIndex(userInfo.avatar);
      setLoading(false);

      let redirectPath = pathname;
      if (pathname === "/login") {
        redirectPath = "/";
      }

      if (!orgName) {
        const orgSkip = localStorage.getItem(`orgSkip-${senderAddress}`);
        if (orgSkip !== "true") {
          navigate("/org");
        } else {
          navigate(redirectPath);
        }
      } else {
        navigate(redirectPath);
      }
    } catch (error) {
      localStorage.removeItem("jwtToken");
      await capsule.logout();
      setAddress("");
      setLoading(false);
      navigate("/login");
      localStorage.setItem("isLogin", "false");
      console.error("handleCheckUserStatus error:", error);
    }
  };

  const handleSetOrgName = (name: string) => {
    setOrgName(name);
  };

  // 切换货币
  const handleSwitchCurrency = () => {
    setShowUsd(!showUsd);
    localStorage.setItem("showUsd", showUsd.toString());
  };

  // 获取usdc余额
  const handleBalance = async () => {
    const balance = await publicClient.readContract({
      abi: erc20Abi,
      address: usdcInfo.address as `0x${string}`,
      functionName: "balanceOf",
      args: [senderAddress as `0x${string}`],
    });

    setUsdcBalance(balance.toString());
  };

  const handleGetCustomer = async () => {
    try {
      const customerData: any = await getCustomer();
      setBridgeCustomer(customerData);
      return customerData;
    } catch (error) {
      console.error("handleGetCustomer error:", error);
    }
  };

  useEffect(() => {
    if (!senderAddress) {
      setUsdcBalance("0");
      return;
    }
    handleBalance();

    const interval = setInterval(() => {
      handleBalance();
    }, 3000);

    return () => clearInterval(interval);
  }, [senderAddress]);

  useEffect(() => {
    if (!publicClient) {
      return;
    }
    handleInit();
  }, [publicClient]);

  useEffect(() => {
    fetchCurrencyInfo();
  }, []);

  return (
    <UserInfoContext.Provider
      value={{
        showUsd,
        address,
        senderAddress,
        orgName,
        smartAccount,
        setAddress,
        handleCheckUserStatus,
        handleSetOrgName,
        usdcBalance,
        currencyInfo,
        countryInfo,
        taxRate,
        showBalance,
        avatarIndex,
        bridgeCustomer,
        handleGetCustomer,
        handleSwitchCurrency,
        handleSetCountryInfo,
        handleSetCurrency,
        handleSetTaxRate,
        handleShowBalance,
        handleSetAvatarIndex,
      }}
    >
      {loading ? <LoadingPage /> : children}
    </UserInfoContext.Provider>
  );
};

export const useUserInfo = () => {
  return useContext(UserInfoContext);
};
